import { useCallback } from 'react';

import { ModelViewerElement } from '@google/model-viewer';
import { Box3 } from 'three/src/math/Box3';
import { Vector3 } from 'three/src/math/Vector3';
import { Quaternion } from 'three/src/math/Quaternion';
import type { ModelScene } from '@google/model-viewer/lib/three-components/ModelScene';

import { RenderGenerationIncomingPayload } from '../../../../../../renderGenerations/renderGenerationsTypes';
import { modelViewerAspectRatioValues } from '../../modelViewerAspectRatioConstants';
import type { ModelViewerAspectRatioValue } from '../../modelViewerAspectRatioConstants';

interface UseModelViewerIncomingPayloadOptions {
  modelViewerEl: ModelViewerElement | null;
}

const RAD_TO_DEG = 180 / Math.PI;
const DEG_TO_RAD = Math.PI / 180;
const PAYLOAD_SCHEMA_VERSION = 1;

function round(value: number, digits = 5): number {
  return +(value || 0).toFixed(digits);
}

function vec3(v: { x: number; y: number; z: number }) {
  return { x: round(v.x), y: round(v.y), z: round(v.z) };
}

function quat(q: { x: number; y: number; z: number; w: number }) {
  return { x: round(q.x), y: round(q.y), z: round(q.z), w: round(q.w) };
}

// three.js Matrix4.elements is column-major. Emit row-major nested arrays so
// the receiver gets a conventional 4x4 without needing to know three's layout.
function matrixToRowMajor(elements: number[] | Float32Array): number[][] {
  const rows: number[][] = [];
  for (let r = 0; r < 4; r += 1) {
    rows.push([
      round(elements[r]),
      round(elements[r + 4]),
      round(elements[r + 8]),
      round(elements[r + 12])
    ]);
  }
  return rows;
}

// Mirror useModelViewer's getHiddenProp: resolve a private model-viewer symbol
// by its description. Robust to minification (matches on .description, not ref).
function getHiddenProp<T = unknown>(
  el: ModelViewerElement | null,
  prop: string
): T | null {
  if (!el) return null;
  const symbol = Object.getOwnPropertySymbols(el).find(
    (s) => s.description === prop
  );
  return symbol ? (el as any)[symbol] : null;
}

/**
 * Builds an engine-agnostic incoming_payload describing the current camera and
 * model placement in the live three.js scene of <model-viewer>. Shared by the
 * render-generation and product lifestyle-generation flows so the contract
 * never diverges between them.
 */
function useModelViewerIncomingPayload({
  modelViewerEl
}: UseModelViewerIncomingPayloadOptions) {
  const buildIncomingPayload = useCallback(
    (
      // The ratio actually sent as aspect_ratio. In "auto" the viewer box is
      // unconstrained, so the measured viewport would contradict the request —
      // the render path re-renders from this payload and would then frame
      // differently than the user saw. Passing the resolved ratio keeps the
      // reported viewport consistent with the request; fov_vertical_deg is
      // left as-is (vertical FOV + target aspect is a coherent contract).
      targetAspectRatio?: ModelViewerAspectRatioValue
    ): RenderGenerationIncomingPayload | null => {
      if (!modelViewerEl) return null;

      // Orbit (human-readable). getCameraOrbit/getCameraTarget/getFieldOfView
      // return the actual rendered values, not the goal attribute string.
      // theta/phi in radians, radius in meters; fov in degrees (vertical).
      const orbit = (modelViewerEl as any).getCameraOrbit?.() as
        | { theta: number; phi: number; radius: number }
        | undefined;
      const fovDeg = (modelViewerEl as any).getFieldOfView?.() as
        | number
        | undefined;

      // Live three.js scene — the camera and model here carry ALL scene
      // transforms (auto-framing, yaw, target shift), so world matrices are
      // authoritative even if the model is not at the origin.
      const scene = getHiddenProp<ModelScene>(modelViewerEl, 'scene');
      const camera = scene?.getCamera?.();
      // scene.model is the actual model object. Hierarchy: scene > pivot
      // (yaw rotation) > target (auto-frame offset) > model. Its matrixWorld
      // already folds in yaw + target offset + the model's own orientation/
      // scale, so it is authoritative regardless of where the model sits.
      const modelRoot = ((scene as any)?.model ?? null) as {
        getWorldPosition(t: Vector3): Vector3;
        getWorldQuaternion(t: Quaternion): Quaternion;
        getWorldScale(t: Vector3): Vector3;
        updateWorldMatrix(p: boolean, c: boolean): void;
        matrixWorld: { elements: number[] | Float32Array };
      } | null;

      // ---- Camera (world space) ----
      const camWorldPos = new Vector3();
      const camWorldQuat = new Quaternion();
      const camForward = new Vector3();

      if (camera) {
        camera.updateMatrixWorld?.(true);
        camera.getWorldPosition?.(camWorldPos);
        camera.getWorldQuaternion?.(camWorldQuat);
        camera.getWorldDirection?.(camForward);
      }

      const cameraUp = (camera as any)?.up ?? new Vector3(0, 1, 0);
      const perspective = camera as any;

      // Derive look_at from the authoritative camera frame
      // (world_position + forward * radius) rather than scene.getTarget(),
      // which lives in model-viewer's own pre-yaw local convention and can
      // disagree with world space after yaw/recentering.
      const radius = orbit?.radius ?? 0;
      const lookAt = camForward.clone().multiplyScalar(radius).add(camWorldPos);

      // ---- Model (world space) ----
      const modelWorldPos = new Vector3();
      const modelWorldQuat = new Quaternion();
      const modelWorldScale = new Vector3(1, 1, 1);
      let matrixWorld: number[][] = [
        [1, 0, 0, 0],
        [0, 1, 0, 0],
        [0, 0, 1, 0],
        [0, 0, 0, 1]
      ];

      if (modelRoot) {
        modelRoot.updateWorldMatrix?.(true, false);
        modelRoot.getWorldPosition?.(modelWorldPos);
        modelRoot.getWorldQuaternion?.(modelWorldQuat);
        modelRoot.getWorldScale?.(modelWorldScale);
        if (modelRoot.matrixWorld?.elements) {
          matrixWorld = matrixToRowMajor(modelRoot.matrixWorld.elements);
        }
      }

      // ---- Bounding box ----
      // Local (placement-independent) size/center come straight from the scene.
      const localBox = (scene?.boundingBox as Box3 | undefined) ?? undefined;
      const localSize = (scene?.size as Vector3 | undefined) ?? undefined;
      const bboxCenter = localBox
        ? localBox.getCenter(new Vector3())
        : undefined;
      // World bbox is computed directly from the live model object (which
      // already carries its full world transform). Do NOT re-apply
      // matrixWorld to the local box — scene.boundingBox is built with the
      // model's own orientation/scale baked in, so that would double-count it.
      const worldBox = (scene as any)?.model
        ? new Box3().setFromObject((scene as any).model)
        : undefined;

      const viewerRect = modelViewerEl.getBoundingClientRect();
      const measuredWidth = viewerRect ? Math.round(viewerRect.width) : 0;
      const measuredHeight = viewerRect ? Math.round(viewerRect.height) : 0;

      // Report the target frame: centre-crop the measured box to the requested
      // ratio, matching what the screenshot flow produces.
      const targetPreset = targetAspectRatio
        ? modelViewerAspectRatioValues[targetAspectRatio]
        : undefined;
      let width = measuredWidth || undefined;
      let height = measuredHeight || undefined;

      if (targetPreset && measuredWidth > 0 && measuredHeight > 0) {
        const target = targetPreset.width / targetPreset.height;
        const current = measuredWidth / measuredHeight;
        width =
          current > target
            ? Math.round(measuredHeight * target)
            : measuredWidth;
        height =
          current < target
            ? Math.round(measuredWidth / target)
            : measuredHeight;
      }

      const aspectRatio =
        width && height ? round(width / height, 4) : undefined;

      // A vertical crop (height reduced) shows less of the scene than the live
      // vertical FOV describes, so the FOV must shrink with it — otherwise the
      // render path re-frames wider than the user saw. A horizontal crop leaves
      // the vertical FOV untouched by construction.
      const verticalCropScale =
        height && measuredHeight > 0 ? height / measuredHeight : 1;
      const effectiveFovDeg =
        fovDeg != null && verticalCropScale < 1
          ? 2 *
            Math.atan(Math.tan((fovDeg / 2) * DEG_TO_RAD) * verticalCropScale) *
            RAD_TO_DEG
          : fovDeg;

      // Keep camera.aspect consistent with the reported viewport: both must
      // describe the same (possibly cropped) frame.
      const cameraAspect = aspectRatio ?? perspective?.aspect;

      return {
        schema_version: PAYLOAD_SCHEMA_VERSION,
        coordinate_system: {
          up: 'y',
          handedness: 'right',
          units: 'meters',
          angles: 'degrees',
          matrix_layout: 'row_major'
        },
        camera: {
          world_position: vec3(camWorldPos),
          look_at: vec3(lookAt),
          up: vec3(cameraUp),
          forward: vec3(camForward),
          world_quaternion: quat(camWorldQuat),
          ...(effectiveFovDeg != null && {
            fov_vertical_deg: round(effectiveFovDeg, 4)
          }),
          ...(cameraAspect != null && { aspect: round(cameraAspect, 5) }),
          ...(perspective?.near != null && {
            near: round(perspective.near, 5)
          }),
          ...(perspective?.far != null && { far: round(perspective.far, 4) }),
          orbit: {
            theta_deg: round((orbit?.theta ?? 0) * RAD_TO_DEG, 4),
            phi_deg: round((orbit?.phi ?? 0) * RAD_TO_DEG, 4),
            radius: round(orbit?.radius ?? 0)
          }
        },
        model: {
          world_position: vec3(modelWorldPos),
          world_quaternion: quat(modelWorldQuat),
          world_scale: vec3(modelWorldScale),
          matrix_world: matrixWorld,
          ...(worldBox &&
            !worldBox.isEmpty() && {
              bbox_world_min: vec3(worldBox.min),
              bbox_world_max: vec3(worldBox.max)
            }),
          ...(localSize && { bbox_size: vec3(localSize) }),
          ...(bboxCenter && { bbox_center: vec3(bboxCenter) }),
          has_ground_shadow: false
        },
        viewport: {
          width,
          height,
          aspect_ratio: aspectRatio
        }
      };
    },
    [modelViewerEl]
  );

  return { buildIncomingPayload };
}

export default useModelViewerIncomingPayload;
